import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectFilter = ({ 
  filters, 
  activeFilters, 
  onFilterChange, 
  onClearFilters,
  projectCount 
}) => {
  const filterCategories = [
    {
      key: 'technology',
      label: 'Technology Stack',
      icon: 'Code2',
      options: filters?.technologies || []
    },
    {
      key: 'type',
      label: 'Project Type',
      icon: 'Layers',
      options: filters?.types || []
    },
    {
      key: 'industry',
      label: 'Industry',
      icon: 'Building',
      options: filters?.industries || []
    }
  ];

  const handleFilterToggle = (category, value) => {
    const currentFilters = activeFilters?.[category] || [];
    const newFilters = currentFilters?.includes(value)
      ? currentFilters?.filter(f => f !== value)
      : [...currentFilters, value];
    
    onFilterChange(category, newFilters);
  };

  const hasActiveFilters = Object.values(activeFilters)?.some(filters => filters?.length > 0);

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Filter" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-primary">Filter Projects</h3>
            <p className="text-sm text-muted-foreground">
              {projectCount} project{projectCount !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>
        
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            iconName="X"
            iconPosition="left"
            iconSize={14}
            onClick={onClearFilters}
          >
            Clear All
          </Button>
        )}
      </div>
      {/* Filter Categories */}
      <div className="space-y-6">
        {filterCategories?.map((category) => (
          <div key={category?.key}>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name={category?.icon} size={16} className="text-muted-foreground" />
              <h4 className="text-sm font-medium text-text-secondary">
                {category?.label}
              </h4>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {category?.options?.map((option) => {
                const isActive = (activeFilters?.[category?.key] || [])?.includes(option);
                return (
                  <button
                    key={option}
                    onClick={() => handleFilterToggle(category?.key, option)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all duration-200 ${
                      isActive
                        ? 'bg-accent text-accent-foreground border-accent shadow-soft'
                        : 'bg-background text-text-secondary border-border hover:border-accent/50 hover:bg-accent/5'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Check" size={16} className="text-success" />
            <span className="text-sm font-medium text-text-secondary">Active Filters</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {Object.entries(activeFilters)?.map(([category, values]) =>
              values?.map((value) => (
                <div
                  key={`${category}-${value}`}
                  className="flex items-center space-x-1 px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded-md"
                >
                  <span>{value}</span>
                  <button
                    onClick={() => handleFilterToggle(category, value)}
                    className="hover:bg-accent/20 rounded-full p-0.5 transition-colors duration-200"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFilter;